import { Link, Navigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ScanBarcode, Warehouse, Package, Truck, Boxes, ListChecks, ClipboardList, Eraser, Flag } from "lucide-react";
import { useTranslation } from "../i18n";
import { isVisible, useHomeModuleVisibility, readUserRole } from "../components/helpers/homeModuleVisibility";
import { useAccess } from "../access/AccessContext";
import FlowLoader from "../components/FlowLoader";
import { useBadgeCounts } from "../hooks/useBadgeCounts";
import "../styles/home.css";

const Home = () => {
    usePageTitle("home");
    const { t } = useTranslation();
    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null
        ? JSON.parse(userData)
        : { name: "", permissions: "", warehouse: "" };
    const role = readUserRole();

    // Tiles follow the access map once it is authoritative (E8: the `app` area
    // key, then each module's own key), and the per-warehouse App Modules map
    // until then. In legacy mode the BE derives the map from those same
    // toggles, so the tiles do not change until cutover.
    const { provided, ready, authoritative, hasAccess, pages } = useAccess();
    // Fallback only where the context cannot decide: no provider (isolated
    // render) or a failed fetch. While the first fetch is in flight we wait
    // rather than flash the fallback tiles and fetch /settings for nothing.
    const pending = provided && !ready;
    const useFallback = !provided || (ready && !authoritative);
    const moduleMap = useHomeModuleVisibility(useFallback ? userObject.warehouse : undefined);
    const show = (key: string) => (authoritative ? hasAccess(key) : isVisible(moduleMap, key, role));
    const appHidden = authoritative && !hasAccess("app");
    const adminOnly = appHidden && hasAccess("admin") && Object.keys(pages ?? {}).some(k => k.startsWith("admin."));

    // Every tile badge comes precomputed from the server in one poll
    // (GET /badges) — no per-tile fetches and no client-side sums. The
    // Pick Face figure is already rolled up server-side across its three
    // sub-modules (Pick Queue + Shelf Drop + Forecast Drop).
    const counts = useBadgeCounts(userObject.warehouse);
    const putAwayCount = counts.putAway.count;
    const pickingItems = counts.pickFace.items;
    const pickingTotes = counts.pickFace.totes;
    const sortItems = counts.zoneStorage.items;
    const sortTotes = counts.zoneStorage.totes;
    const dispatchItemCount = counts.dispatch.items;
    const dispatchToteCount = counts.dispatch.count;

    const showPutAway = show("put_away");
    const showPickFace = show("pick_face");
    const flaggedPicksUnits = show("pick_face.flagged_picks") ? counts.flaggedPicks.count : 0;
    const showZoneStorage = show("zone_storage");
    const showDispatch = show("dispatch");

    // `app` at none: a desk-only user lands on the Admin side; someone with
    // neither area gets told so instead of an empty grid.
    if (pending) return <FlowLoader className="protected-route-loader" />;
    if (adminOnly) return <Navigate to="/admin" replace />;
    if (appHidden) {
        return (
            <div className="page-data">
                <div className="page-title-bar">
                    <h1 className="page-title">{t("home")}</h1>
                </div>
                <p className="home-no-access" role="status">{t("no_area_access")}</p>
            </div>
        );
    }

    return (
        <div className="page-data">
            <div className="page-title-bar">
                <h1 className="page-title">{t("home")}</h1>
            </div>
            <div className="home-grid">
                {show("goods_in") && (
                    <Link to="/goods-in" className="home-card home-card-orange">
                        <div className="home-card-icon">
                            <ScanBarcode size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("goods_in")}</div>
                    </Link>
                )}

                {show("conveyor") && (
                    <Link to="/conveyor" className="home-card home-card-green">
                        <div className="home-card-icon">
                            <Package size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("conveyor")}</div>
                    </Link>
                )}

                {showPutAway && (
                    <Link to="/put-away" className="home-card home-card-pink">
                        {putAwayCount > 0 && (
                            <span className="home-card-badge">{putAwayCount}</span>
                        )}
                        <div className="home-card-icon">
                            <Boxes size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("put_away")}</div>
                    </Link>
                )}

                {show("stock_control") && (
                    <Link to="/stock-control" className="home-card home-card-red">
                        <div className="home-card-icon">
                            <ClipboardList size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("stock_control")}</div>
                    </Link>
                )}

                {showPickFace && (
                    <Link to="/pick-face" className="home-card home-card-cyan">
                        {(pickingItems > 0 || pickingTotes > 0 || flaggedPicksUnits > 0) && (
                            <div className="home-card-badge-group">
                                {pickingItems > 0 && (
                                    <span className="home-card-badge" aria-label={`${pickingItems} items`}>
                                        {pickingItems}
                                    </span>
                                )}
                                {pickingTotes > 0 && (
                                    <span className="home-card-badge home-card-badge--totes" aria-label={`${pickingTotes} totes`}>
                                        {pickingTotes}
                                    </span>
                                )}
                                {/* Flagged Picks (2026-10-09): units a leader
                                    still has to fetch from high racking. Only
                                    for people who can open that tile. */}
                                {flaggedPicksUnits > 0 && (
                                    <span className="home-card-badge home-card-badge--flagged" aria-label={`${flaggedPicksUnits} flagged picks`} data-testid="home-flagged-picks-badge">
                                        <Flag size={12} strokeWidth={3} />
                                        {flaggedPicksUnits}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="home-card-icon">
                            <ListChecks size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("pick_face")}</div>
                    </Link>
                )}

                {showZoneStorage && (
                    <Link to="/zonestorage" className="home-card home-card-blue">
                        {(sortItems > 0 || sortTotes > 0) && (
                            <div className="home-card-badge-group">
                                {sortItems > 0 && (
                                    <span className="home-card-badge" aria-label={`${sortItems} items`}>
                                        {sortItems}
                                    </span>
                                )}
                                {sortTotes > 0 && (
                                    <span className="home-card-badge home-card-badge--totes" aria-label={`${sortTotes} totes`}>
                                        {sortTotes}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="home-card-icon">
                            <Warehouse size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("zone_storage")}</div>
                    </Link>
                )}

                {showDispatch && (
                    <Link to="/dispatch/menu" className="home-card home-card-purple">
                        {(dispatchItemCount > 0 || dispatchToteCount > 0) && (
                            <div className="home-card-badge-group">
                                {dispatchItemCount > 0 && (
                                    <span className="home-card-badge" aria-label={`${dispatchItemCount} items`}>
                                        {dispatchItemCount}
                                    </span>
                                )}
                                {dispatchToteCount > 0 && (
                                    <span className="home-card-badge home-card-badge--totes" aria-label={`${dispatchToteCount} totes`}>
                                        {dispatchToteCount}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="home-card-icon">
                            <Truck size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("dispatch")}</div>
                    </Link>
                )}

                {show("wipe") && (
                    <Link to="/wipe" className="home-card home-card-amber">
                        <div className="home-card-icon">
                            <Eraser size={40} strokeWidth={1.5} />
                        </div>
                        <div className="home-card-title">{t("wipe")}</div>
                    </Link>
                )}
            </div>
        </div>
    );
};

export default Home;
